/**
 * Round Tables API client — all calls proxied through CF Functions at /api/round-tables/*.
 */

import type {
  RoundTable,
  RoundTableStatus,
  CreateRoundTableData,
  SpeakData,
  ResolveData,
} from '../types/round-tables'

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text()
    throw new Error(text || `Request failed (${response.status})`)
  }
  return response.json()
}

function getCsrfToken(): string {
  const match = document.cookie.match(/csrf_token=([^;]+)/)
  if (!match) throw new Error('CSRF token not found — session may have expired')
  return match[1]
}

function mutationHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCsrfToken(),
  }
}

export async function fetchRoundTables(status?: RoundTableStatus): Promise<RoundTable[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : ''
  const res = await fetch(`/api/round-tables${qs}`, { credentials: 'include' })
  const data = await handleResponse<{ round_tables: RoundTable[] } | RoundTable[]>(res)
  return Array.isArray(data) ? data : (data as { round_tables: RoundTable[] }).round_tables ?? []
}

export async function fetchRoundTable(id: string): Promise<RoundTable> {
  const res = await fetch(`/api/round-tables/${encodeURIComponent(id)}`, {
    credentials: 'include',
  })
  return handleResponse<RoundTable>(res)
}

export async function createRoundTable(data: CreateRoundTableData): Promise<RoundTable> {
  const res = await fetch('/api/round-tables', {
    method: 'POST',
    credentials: 'include',
    headers: mutationHeaders(),
    body: JSON.stringify(data),
  })
  return handleResponse<RoundTable>(res)
}

export async function speakInRoundTable(id: string, data: SpeakData): Promise<{ success: boolean }> {
  const res = await fetch(`/api/round-tables/${encodeURIComponent(id)}?action=speak`, {
    method: 'POST',
    credentials: 'include',
    headers: mutationHeaders(),
    body: JSON.stringify(data),
  })
  return handleResponse<{ success: boolean }>(res)
}

export async function resolveRoundTable(id: string, data: ResolveData): Promise<{ success: boolean }> {
  const res = await fetch(`/api/round-tables/${encodeURIComponent(id)}?action=resolve`, {
    method: 'POST',
    credentials: 'include',
    headers: mutationHeaders(),
    body: JSON.stringify(data),
  })
  return handleResponse<{ success: boolean }>(res)
}
